import { api } from '../../core/network/api';

export type HeroClass = 'WARRIOR' | 'PALADIN' | 'ARCHER' | 'MAGE';

/** Display names for the 4 classes (code keys unchanged for battle balance). */
export const CLASS_LABELS: Record<HeroClass, string> = {
  WARRIOR: 'Enforcer',
  PALADIN: 'Capo',
  ARCHER: 'Triggerman',
  MAGE: 'Fixer',
};

export interface HeroProfile {
  hero: {
    id: string;
    name: string;
    class: HeroClass;
    stats: { hp: number; maxHp: number; attack: number; defense: number; speed: number; magic: number };
    nerve: { current: number; max: number };
    position: { col: number; row: number };
    /** Fractional regen anchors + rates for client-side countdowns. */
    regen: {
      hp: { exact: number; perMin: number };
      nerve: { exact: number; perMin: number };
    };
  };
  progression: {
    username: string | null;
    level: number;
    xp: number;
    cash: number;
    goldBars: number;
  };
}

/** GET /api/heroes/me — returns null if the player has no hero yet (404). */
export async function fetchHero(): Promise<HeroProfile | null> {
  try {
    return await api.get<HeroProfile>('/heroes/me');
  } catch (e) {
    if ((e as { status?: number }).status === 404) return null;
    throw e;
  }
}

/** POST /api/heroes — create the account's hero. */
export function createHero(name: string, cls: HeroClass): Promise<HeroProfile> {
  return api.post<HeroProfile>('/heroes', { name, class: cls });
}

export interface EffectiveStats {
  base: { attack: number; defense: number; magic: number; speed: number; hp: number; maxHp: number };
  gear: { attack: number; defense: number; magic: number; speed: number; hp: number };
  effective: { attack: number; defense: number; magic: number; speed: number; maxHp: number };
  sets: { name: string; pieces: number; description: string }[];
  boosts: { cashRush: boolean; xpBooster: boolean; revive: boolean };
}

/** GET /api/heroes/stats — effective combat power (base + gear), sets, boosts. */
export const fetchStats = () => api.get<EffectiveStats>('/heroes/stats');

/** Cosmetic XP curve for the Level progress bar — mirrors the core
 *  EconomyService default (`XP_PER_LEVEL`, env-overridable, defaults to 100).
 *  Never used for combat math, purely a display progress bar. */
export const XP_PER_LEVEL = 100;

/** Current-level XP progress (0..XP_PER_LEVEL) from cumulative XP. */
export function xpProgress(xp: number): { current: number; needed: number } {
  const needed = XP_PER_LEVEL;
  const current = ((xp % needed) + needed) % needed;
  return { current, needed };
}
